import React, { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ShareSafari from "./sharesafari/ShareSafari";
import ShareSafariFixed from "./fixedsafari/ShareSafariFixed";
import apiCall, { apiCall2 } from "components/Api/api";
import {
  useSharedSafariContext,
} from "provider/ShareSafariProvider";
import BannerSafari from "./BannerSafari";
import ShareSafariHeader from "./ShareSafariHeader";
import SafariViewHeaderSkeleton from "components/common/Skeleton/safari/SafariViewHeaderSkeleton";
import DiscussionSkeleton from "components/common/Skeleton/safari/DiscussionSkeleton";
import { useAuth } from "provider/AuthContext";
import { handleError } from "components/Api/handleError";
import MarkFlagModal from "./common/MarkFlagModal";
import OrganizeSharedSafariModal from "../organizedsafri/OrganizeSharedSafariModal";

const ShareSafariView = () => {
  return (
    <ShareSafariContent />
  );
};

const ShareSafariContent = () => {
  const { safariSlug } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { safariViewData, toggleOragnize, updateSafariListing, safariViewLoading, reportContentToggle,
    pageIndexUserComment,
    sharedSafariDispatch } = useSharedSafariContext();
  const fetchedSlugRef = useRef(null);

  useEffect(() => {
    let isCancelled = false;

    const fetchSafariView = async () => {
      try {
        sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_LOADING", payload: true });

        const response = await apiCall({
          url: `sharesafari/${safariSlug}/view`,
          method: "GET",
        });

        if (response.status !== 200) throw new Error(`Unexpected status: ${response.status}`);

        const apiData = response.data;

        if (apiData?.errors) {
          sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_ERROR", payload: apiData.errors });
          return;
        }

        const data = apiData?.data;

        sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_DETAILS", payload: data });
        sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_INTRESTED_USER_URL", payload: data?.urls?.intrested_users });
        sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_INTRESTED_COMMENTS_URL", payload: data?.urls?.comments });

        fetchedSlugRef.current = safariSlug;

        if (updateSafariListing) {
          sharedSafariDispatch({ type: "UPDATE_SHARED_SAFARI_LISTING", payload: false });
        }
      } catch (error) {
        sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_ERROR", payload: "Network error occurred." });
        handleError(error, logout, navigate);
      } finally {
        sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_LOADING", payload: false });
      }
    };

    const shouldFetch =
      updateSafariListing ||
      (
        safariSlug &&
        (!safariViewData || safariViewData.slug !== safariSlug) &&
        fetchedSlugRef.current !== safariSlug
      );

    if (shouldFetch) {
      sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_USERS_COMMENTS", payload: [] });
      sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_INTRESTED_USERS", payload: { users: [], total: 0 } });
      fetchSafariView();
    }

    return () => {
      isCancelled = true;
    };
  }, [safariSlug, updateSafariListing]);

  //intrested users
  useEffect(() => {
    let isCancelled = false;

    const fetchAllUsers = async (url) => {
      try {
        let allUsers = [];
        let currentPage = 1, totalPages = 1, totalUsers = 0;

        do {
          const response = await apiCall2({ url: `${url}?page=${currentPage}&pageSize=10`, method: "GET" });
          if (response.status !== 200) throw new Error(`Unexpected status: ${response.status}`);

          const data = response.data.intrested_users?.data || [];
          totalUsers = response.data.intrested_users?.summary?.total || allUsers.length;
          totalPages = response.data.intrested_users?.summary?.total_page || 1;
          const summary = response?.data?.intrested_users?.summary;
          sharedSafariDispatch({
            type: "SF_INTRESTER_USER_SUMMARY",
            payload: {
              total: summary.total,
              page: summary.page,
              pageSize: summary.pageSize,
              total_page: summary.total_page,
            },
          });
          sharedSafariDispatch({
            type: "SF_INTRESTER_USER_PAGE_INDEX",
            payload: currentPage,
          });

          allUsers = [...allUsers, ...data];
          currentPage++;
        } while (currentPage <= totalPages && currentPage <= 3);

        if (!isCancelled) {
          sharedSafariDispatch({
            type: "SET_SHARED_SAFARI_VIEW_INTRESTED_USERS",
            payload: { users: allUsers, total: totalUsers },
          });
        }
      } catch (error) {
        handleError(error, logout, navigate);
      }
    };

    if (safariViewData?.urls?.intrested_users) {
      sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_INTRESTED_USERS", payload: { users: [], total: 0 } });
      fetchAllUsers(safariViewData.urls.intrested_users);
    }

    return () => {
      isCancelled = true;
    };
  }, [safariViewData?.urls?.intrested_users]);

  // user comments
  useEffect(() => {
    let isCancelled = false;

    const fetchAllComments = async (url) => {
      try {
        const response = await apiCall2({ url: `${url}?page=${pageIndexUserComment}&pageSize=10&sort=-created_at`, method: "GET" });
        if (response.status !== 200) throw new Error(`Unexpected status: ${response.status}`);

        const data = response.data.comments?.data || [];
        const summary = response?.data?.comments?.summary;

        if (pageIndexUserComment === 1) {
          sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_USERS_COMMENTS", payload: data });
        } else {
          sharedSafariDispatch({
            type: "APPEND_USERS_COMMENTS_DATA",
            payload: data,
          });
        }

        sharedSafariDispatch({
          type: "SF_USER_COMMENTS_SUMMARY",
          payload: {
            total: summary.total,
            page: summary.page,
            pageSize: summary.pageSize,
            total_page: summary.total_page,
          },
        });
      } catch (error) {
        handleError(error, logout, navigate);
      }
    };

    if (safariViewData?.urls?.comments) {
      // sharedSafariDispatch({ type: "SET_SHARED_SAFARI_VIEW_USERS_COMMENTS", payload: [] });
      fetchAllComments(safariViewData.urls.comments);
    }

    return () => {
      isCancelled = true;
    };
  }, [safariViewData?.urls?.comments, pageIndexUserComment]);

  // fetch review
  useEffect(() => {
    if (!safariViewData?.partner?.review_url?.reviews) return;

    let isCancelled = false;
    const fetchAllReviews = async () => {
      try {
        sharedSafariDispatch({ type: "SET_SAFARI_OPERATOR_RATING_LOADING", payload: true });
        const response = await apiCall2({
          url: `${safariViewData?.partner?.review_url?.reviews}?page=${1}&pageSize=3`,
          method: "GET",
        });

        if (response.status !== 200) {
          throw new Error(`Unexpected status: ${response.status}`);
        }

        const apiData = response.data;

        const review = apiData?.reviews?.data;
        sharedSafariDispatch({ type: "SET_SAFARI_OPERATOR_RATING", payload: review });


      } catch (error) {
        handleError(error, logout, navigate);
      } finally {
        sharedSafariDispatch({ type: "SET_SAFARI_OPERATOR_RATING_LOADING", payload: false });
      }
    };
    if (safariViewData?.partner?.review_url?.reviews) {
      fetchAllReviews();
    }
    return () => {
      isCancelled = true;
    };
  }, [safariViewData?.partner?.review_url?.reviews]);


  return (
    <>
      <BannerSafari />
      {safariViewLoading ? <SafariViewHeaderSkeleton /> : <ShareSafariHeader />}
      {safariViewLoading ? (
        <DiscussionSkeleton />
      ) : (
        <>
          {safariViewData?.types === "Fixed Departure" && (
            <>
              <ShareSafariFixed />
            </>
          )}
          {safariViewData?.types === "Share Safari" && (
            <>
              <ShareSafari />
            </>
          )}
        </>
      )}
      {reportContentToggle && <MarkFlagModal type="shareSafariReport" />}
      {toggleOragnize && <OrganizeSharedSafariModal />}
    </>
  );
};

export default ShareSafariView;